// A toast only fires on a user action (copy failed, like failed), never on first
// paint. It used to go through `@heroui/react`'s `addToast`, but that needs a
// mounted HeroUI <ToastProvider> to render — and the public read/explore
// surfaces dropped that provider because the ~249 KB HeroUI barrel was sitting on
// their critical path. Without a provider, `addToast` silently no-ops. So
// `showToast` is now a self-contained portal toast: it self-mounts on first call,
// needs no provider, pulls in zero extra chunks, and works on every surface.
// Signature is unchanged so callers (useCopyGuide, useGuideLikes) don't change.

export type ToastArgs = {
  title?: string
  description?: string
  // Mirrors the subset of HeroUI's `color` the callers actually pass. `danger`
  // is the only one in use today; the rest keep the type honest for future use.
  color?: `danger` | `success` | `warning` | `default` | `primary` | `secondary`
}

// Accent ring per color, layered on the neutral themed surface. Kept to Tailwind
// utility classes already shipped in styles.css so the toast needs no extra CSS.
const ACCENT: Record<NonNullable<ToastArgs[`color`]>, string> = {
  danger: `border-l-red-500`,
  success: `border-l-green-500`,
  warning: `border-l-amber-500`,
  default: `border-l-border`,
  primary: `border-l-primary`,
  secondary: `border-l-secondary`,
}

let container: HTMLDivElement | null = null

function getContainer(): HTMLDivElement {
  if (container && document.body.contains(container)) return container
  const el = document.createElement(`div`)
  el.className = `fixed bottom-4 right-4 z-[9999] flex flex-col items-end gap-2 pointer-events-none`
  document.body.appendChild(el)
  container = el
  return el
}

// Async + Promise-returning to keep the exact `(args) => Promise<void>` shape the
// previous HeroUI-backed implementation exposed (callers `await`/`void` it).
export async function showToast(args: ToastArgs): Promise<void> {
  if (typeof document === `undefined`) return
  const root = getContainer()

  const toast = document.createElement(`div`)
  toast.className = `pointer-events-auto w-[min(22rem,calc(100vw-2rem))] rounded-xl border border-l-4 ${
    ACCENT[args.color ?? `default`]
  } bg-background text-foreground shadow-lg px-4 py-3 translate-y-2 opacity-0 transition-all duration-200`
  toast.setAttribute(`role`, `status`)
  toast.setAttribute(`aria-live`, `polite`)

  if (args.title) {
    const title = document.createElement(`p`)
    title.className = `text-sm font-semibold`
    // textContent (not innerHTML): the message is app-authored today, but this
    // keeps it injection-safe if a server string ever flows through here.
    title.textContent = args.title
    toast.appendChild(title)
  }
  if (args.description) {
    const desc = document.createElement(`p`)
    desc.className = `text-xs text-muted-foreground leading-snug mt-0.5`
    desc.textContent = args.description
    toast.appendChild(desc)
  }

  root.appendChild(toast)
  // Next frame: flip to the visible state so the enter transition runs.
  requestAnimationFrame(() => {
    toast.classList.remove(`translate-y-2`, `opacity-0`)
  })

  const remove = () => {
    toast.classList.add(`opacity-0`, `translate-y-2`)
    setTimeout(() => {
      toast.remove()
      if (container && container.childElementCount === 0) {
        container.remove()
        container = null
      }
    }, 200)
  }
  setTimeout(remove, 5000)
}
